import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import {elapsedCallSeconds, formatCallTime} from "./callTime";
import {showSnackbar} from "./Snackbar";

const copyCallerId = async (entry) => {
  if (String(entry.id).includes("null")) {
    showSnackbar("Anonymous caller.", "warning");
    return;
  }
  try {
    await navigator.clipboard.writeText(entry.id);
    showSnackbar("Caller ID copied.");
  } catch (_) {
    showSnackbar("Caller ID could not be copied.", "warning");
  }
};

export default function MonitoredCallTile({
  entry,
  now,
  reviewerMonitoring,
}) {
  return <Box
    sx={{
      mx: 0.5,
      flex: "0 0 auto",
      width: {xs: "100%", sm: 158, md: 166, lg: 174, xl: 182},
      scrollSnapAlign: "start",
    }}
  >
    <Paper
      component="section"
      aria-label={`Listening to queue ${entry.queueNumber}`}
      elevation={0}
      sx={{
        width: "100%",
        height: {xs: 270, sm: 300},
        p: 1.1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "2px solid",
        borderColor: "primary.main",
        borderRadius: "10px",
        bgcolor: (theme) => theme.palette.mode === "dark" ?
          "#163a32" : "#e9f8f2",
        boxShadow: "0 12px 28px rgba(0, 109, 91, 0.18)",
        animation: "monitorBorder 2s ease-in-out infinite",
        "@keyframes monitorBorder": {
          "50%": {
            boxShadow: "0 12px 32px rgba(0, 109, 91, 0.3)",
          },
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{width: "100%"}}
      >
        <Chip
          size="small"
          label={entry.queueNumber}
          sx={{fontWeight: 700}}
        />
        <Box sx={{flex: 1}} />
        <HeadphonesRoundedIcon
          color="primary"
          sx={{fontSize: 21}}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={0.25}
        alignItems="end"
        aria-hidden="true"
        sx={{height: 28, mt: 2}}
      >
        {[0, 1, 2, 3].map((bar) => <Box
          key={bar}
          sx={{
            width: 4,
            height: 8 + bar * 4,
            borderRadius: 4,
            bgcolor: "primary.main",
            transformOrigin: "bottom",
            animation: `monitorWave 900ms ${bar * 110}ms ease-in-out infinite alternate`,
            "@keyframes monitorWave": {
              from: {transform: "scaleY(.45)", opacity: .55},
              to: {transform: "scaleY(1)", opacity: 1},
            },
          }}
        />)}
      </Stack>

      <Typography
        variant="caption"
        color="primary.main"
        sx={{mt: 0.8, fontWeight: 800, letterSpacing: ".03em"}}
      >
        LISTENING NOW
      </Typography>
      <Typography
        noWrap
        sx={{
          mt: 1,
          width: "100%",
          textAlign: "center",
          fontSize: ".84rem",
          fontWeight: 750,
        }}
      >
        {entry.answeredBy || "Agent"}
      </Typography>
      <Typography
        variant="caption"
        color="success.main"
        sx={{mt: .35, fontWeight: 700, fontVariantNumeric: "tabular-nums"}}
      >
        Live · {entry.timeAnswered ?
          formatCallTime(elapsedCallSeconds(entry.timeAnswered, now)) :
          "Connected"}
      </Typography>

      <Stack
        direction="row"
        spacing={0.7}
        alignItems="center"
        sx={{width: "100%", mt: 1.4, px: 0.4}}
      >
        <VolumeDownRoundedIcon
          color="primary"
          sx={{fontSize: 17}}
        />
        <Slider
          size="small"
          min={0}
          max={1}
          step={0.05}
          value={reviewerMonitoring.monitorVolume}
          onChange={(_, value) => reviewerMonitoring.setMonitorVolume(value)}
          aria-label="Listening volume"
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          sx={{py: 0.6}}
        />
        <VolumeUpRoundedIcon
          color="primary"
          sx={{fontSize: 17}}
        />
      </Stack>

      <Tooltip
        title={String(entry.id).includes("null") ?
          "Anonymous caller" : "Copy caller ID"}
      >
        <Typography
          component="button"
          onClick={() => copyCallerId(entry)}
          noWrap
          sx={{
            mt: "auto",
            mb: 0.8,
            maxWidth: "100%",
            p: 0,
            color: "text.secondary",
            bgcolor: "transparent",
            border: 0,
            fontSize: ".68rem",
            cursor: "pointer",
          }}
        >
          {String(entry.id).includes("null") ? "Anonymous" : entry.id}
        </Typography>
      </Tooltip>
      <Button
        fullWidth
        size="small"
        color="error"
        variant="outlined"
        startIcon={<StopRoundedIcon />}
        onClick={() => reviewerMonitoring.listen(entry)}
        sx={{minHeight: 38, borderRadius: "8px", fontWeight: 700}}
      >
        Stop listening
      </Button>
    </Paper>
  </Box>;
}
